/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { configuration } from "@/lib/configuration";
import { EXTERNAL_APIS } from "@/lib/constants";
import * as Sentry from "@sentry/react";

export interface IPDetailsResponse {
  ip: string;
  org?: string;
  city?: string;
  country_code?: string;
  country_name?: string;
  continent_code?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
  /** Comma-separated BCP 47 language tags for the IP's region (e.g. "pt-BR,en"). */
  languages?: string;
  /** B4: State/region name from the server-side GeoIP lookup. Replaces the
   *  browser-side call to the public Nominatim service (blocked by CSP). */
  region?: string;
  /** B4: Postal/ZIP code from the server-side GeoIP lookup. */
  postal_code?: string;
}

// DD-7: Module-level cache — first caller (languageInitPromise in main.tsx) populates
// the cache; subsequent callers (MyPCInfo) get the cached result without a second
// network request to the internal /api/geo/ endpoint — no third-party dependency.
let cachedResponse: IPDetailsResponse | null = null;

/** Resets the module-level cache. Intended for use in tests only. */
export const resetIPDetailsCache = (): void => {
  cachedResponse = null;
};

/**
 * IP geolocation fetch with a 5-second hard timeout.
 * If /api/geo/ is unreachable, the fetch is aborted and the caller falls back
 * to English — the UI never freezes waiting on geolocation.
 */
const TIMEOUT_MS = 5_000;

const IPDetails = async (): Promise<IPDetailsResponse> => {
  if (cachedResponse) return cachedResponse;

  const url = `${configuration.backendURL}${EXTERNAL_APIS.GEO_PATH}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);

    if (!response.ok) {
      Sentry.captureException(
        new Error(`Geo API error: ${response.status}`),
        {
          tags: { api_method: "GET (geo)" },
          contexts: { request: { url, method: "GET" } },
        },
      );
      return { ip: `HTTP ${response.status}` };
    }

    const data = (await response.json()) as IPDetailsResponse;
    cachedResponse = data;
    return data;
  } catch (error) {
    clearTimeout(timer);
    const isTimeout =
      error instanceof DOMException && error.name === "AbortError";
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
      {
        tags: {
          api_method: "GET (geo)",
          is_timeout: String(isTimeout),
        },
        contexts: { request: { url, method: "GET" } },
      },
    );
    return { ip: isTimeout ? "Timeout" : "Network error" };
  }
};

export default IPDetails;
